import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Modal, ModalHeader, ModalBody } from 'shards-react';
import moment from 'moment-timezone';
import PageTitle from '../../components/common/PageTitle';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faSearch, faTimes, faFile } from '@fortawesome/free-solid-svg-icons';
import DownloadButton from '../../components/common/DownloadButton';
import ActivityIndicator from '../../components/common/ActivityIndicator';
import { startOfDay, endOfDay, sub } from 'date-fns';
import generatePDF from 'react-to-pdf';
import 'react-datepicker/dist/react-datepicker.css';

const PotentialViolations = (props) => {
  const [violations, setViolations] = useState([]);
  const [search, setSearch] = useState('');
  const [client, setClient] = useState(props?.clientFilter || 'all');
  const [location, setLocation] = useState(props?.locationFilter || 'all');
  const [startDate, setStartDate] = useState(() => {
    const savedStartDate = localStorage.getItem('enforcePlus_startDate');
    return props?.dateRangeFilter?.startDate
      ? new Date(props.dateRangeFilter.startDate)
      : savedStartDate
        ? new Date(savedStartDate)
        : startOfDay(new Date());
  });
  const [endDate, setEndDate] = useState(() => {
    const savedEndDate = localStorage.getItem('enforcePlus_endDate');
    return props?.dateRangeFilter?.endDate
      ? new Date(props.dateRangeFilter.endDate)
      : savedEndDate
        ? new Date(savedEndDate)
        : endOfDay(new Date());
  });
  const [showDatePickerModal, setShowDatePickerModal] = useState(false);
  const [activeCalendar, setActiveCalendar] = useState(null);
  const [violationToRemove, setViolationToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    // Initial load
    props.getAllViolations(startDate, endDate, { clientId: client, locationId: location });
    props.setDateRangeFilter({ startDate, endDate });
    document.title = 'Potential Violations: EnforcePlus admin';
  }, []);

  useEffect(() => {
    // Refetch when filters change
    props.getAllViolations(startDate, endDate, { clientId: client, locationId: location });
  }, [startDate, endDate, client, location]);

  useEffect(() => {
    // Update local state when store updates
    if (props.violations) {
      setViolations(props.violations);
    }
  }, [props.violations]);

  // Memoized formatted date range
  const formattedDateRange = useMemo(() => {
    return `${moment(startDate).format('D MMM')} - ${moment(endDate).format('D MMM YY')}`;
  }, [startDate, endDate]);

  // Date change handler
  const handleDateChange = useCallback((type, date) => {
    if (!date) return;
    if (type === 'start') {
      setStartDate(date);
      localStorage.setItem('enforcePlus_startDate', date.toISOString());
    } else {
      setEndDate(date);
      localStorage.setItem('enforcePlus_endDate', date.toISOString());
    }
  }, []);

  // Quick preset handler
  const handlePresetClick = useCallback((preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = startOfDay(today);
        end = endOfDay(today);
        break;
      case 'yesterday':
        start = startOfDay(sub(today, { days: 1 }));
        end = endOfDay(sub(today, { days: 1 }));
        break;
      case 'lastWeek':
        start = startOfDay(sub(today, { days: 6 }));
        end = endOfDay(today);
        break;
      case 'lastMonth':
        start = startOfDay(sub(today, { days: 29 }));
        end = endOfDay(today);
        break;
      default:
        return;
    }
    setStartDate(start);
    setEndDate(end);
    localStorage.setItem('enforcePlus_startDate', start.toISOString());
    localStorage.setItem('enforcePlus_endDate', end.toISOString());
    setShowDatePickerModal(false);
  }, []);

  // Client-side filtering
  const filterViolations = useCallback(() => {
    if (!violations || violations.length === 0) return [];
    let list = [...violations];

    if (search) {
      const s = search.toLowerCase();
      list = list.filter(v => (
        (v.license_plate || '').toLowerCase().includes(s)
        || (v.license_plate_state || '').toLowerCase().includes(s)
        || (v.vehicle || '').toLowerCase().includes(s)
        || (v.path || '').toLowerCase().includes(s)
      ));
    }

    if (client !== 'all') {
      props.setClientFilter(client);
      list = list.filter(v => v.client === client);
    } else {
      props.setClientFilter('all');
    }

    if (location !== 'all') {
      props.setLocationFilter(location);
      list = list.filter(v => v.location === location);
    } else {
      props.setLocationFilter('all');
    }

    return list;
  }, [violations, search, client, location]);

  // CSV data
  const csvHeader = useMemo(() => (
    ['License plate', 'Date/Time', 'State', 'Make', 'Model', 'Color']
  ), []);

  const generateFileData = useCallback(() => {
    const rows = filterViolations().map(v => [
      v.license_plate || '',
      v.scanned ? moment(v.scanned).format('MM/DD/Y h:mm a') : (v.created_at ? moment(v.created_at).format('MM/DD/Y h:mm a') : ''),
      v.license_plate_state || '',
      v.make || '',
      v.model || '',
      v.color || ''
    ]);
    return [csvHeader, ...rows];
  }, [filterViolations]);

  const handleConfirmRemove = async () => {
    if (!violationToRemove) return;
    try {
      setIsRemoving(true);
      await props.deleteViolation(violationToRemove.client, violationToRemove.location, violationToRemove.id);
      setViolationToRemove(null);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Container fluid className="main-content-container px-4">
      <style>
        {`
          .filter-active .select__control {
            border-color: #21AA73 !important;
            border-width: 2px !important;
            box-shadow: 0 0 5px rgba(33, 170, 115, 0.2) !important;
            background-color: rgba(33, 170, 115, 0.05) !important;
          }
        `}
      </style>
      <Row noGutters className="page-header py-4">
        <PageTitle title="Potential violations" className="text-sm-left mb-3" />
      </Row>

      <Row>
        <Col>
          <Card small className="mb-4" style={{
            borderRadius: '6px',
            boxShadow: '0 1px 10px rgba(0,0,0,0.05)',
            border: '1px solid #eaedf3',
            margin: '0 0 20px 0'
          }}>
            <CardHeader className="border-bottom">
              <Row>
                <Col xs={12} className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <h6 className="m-0 font-weight-bold d-flex align-items-center">
                      <FontAwesomeIcon icon={faFile} className="mr-2" style={{color: '#21AA73', opacity: 0.8}} />
                      ({filterViolations().length})
                    </h6>
                  </div>
                  <div className="d-flex align-items-center">
                    {/* Client selector (super admin only) */}
                    {props && props.user && props.user.type === 'super' && (
                      <div style={{ width: '150px', marginRight: '10px' }}>
                        <Select
                          id="violationClients"
                          className={client !== 'all' ? 'basic-select filter-active' : 'basic-select'}
                          classNamePrefix="select"
                          placeholder="All clients"
                          isClearable={false}
                          isSearchable={true}
                          value={client === 'all'
                            ? { value: 'all', label: 'All clients' }
                            : props.clients && props.clients.find(c => c.id === client)
                              ? { value: client, label: props.clients.find(c => c.id === client).name }
                              : { value: 'all', label: 'All clients' }
                          }
                          onChange={(selectedOption) => setClient(selectedOption.value)}
                          options={[
                            { value: 'all', label: 'All clients' },
                            ...props.clients && props.clients.sort((a, b) => a.name.localeCompare(b.name)).map(c => ({
                              value: c.id,
                              label: c.name
                            })) || []
                          ]}
                        />
                      </div>
                    )}

                    {/* Date Range Button */}
                    <Button
                      theme="white"
                      className="d-flex align-items-center mr-2"
                      style={{
                        borderColor: '#e1e5eb',
                        height: '38px',
                        padding: '0 12px',
                        fontSize: '14px'
                      }}
                      onClick={() => setShowDatePickerModal(true)}
                    >
                      <FontAwesomeIcon icon={faCalendar} className="mr-2" style={{ color: '#21AA73' }} />
                      {formattedDateRange}
                    </Button>

                    {/* Location Selector */}
                    {!(props.user?.embed && props.user?.location) && (
                      <div style={{ width: '150px', marginRight: '10px' }}>
                        <Select
                          id="violationLocations"
                          className={location !== 'all' ? 'basic-select filter-active' : 'basic-select'}
                          classNamePrefix="select"
                          placeholder="All locations"
                          isClearable={false}
                          isSearchable={true}
                          value={location === 'all'
                            ? { value: 'all', label: 'All locations' }
                            : props.locations && props.locations.find(loc => loc.id === location)
                              ? { value: location, label: props.locations.find(loc => loc.id === location).name }
                              : { value: 'all', label: 'All locations' }
                          }
                          onChange={(selectedOption) => setLocation(selectedOption.value)}
                          options={[
                            { value: 'all', label: 'All locations' },
                            ...props.locations && props.locations.sort((a, b) => a.name.localeCompare(b.name)).map(loc => ({
                              value: loc.id,
                              label: loc.name
                            })) || []
                          ]}
                        />
                      </div>
                    )}

                    {/* Search Box */}
                    <div className="search-container mr-3" style={{ position: 'relative', maxWidth: '100%', flex: '1' }}>
                      <input
                        type="text"
                        placeholder="Search violations..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 32px 8px 36px',
                          fontSize: '14px',
                          border: '1px solid #dfe3e9',
                          borderRadius: '4px',
                          backgroundColor: '#f8f9fa',
                          transition: 'all 0.2s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = '#fff';
                          e.target.style.boxShadow = '0 0 0 0.2rem rgba(33, 170, 115, 0.1)';
                          e.target.style.borderColor = 'rgba(33, 170, 115, 0.4)';
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = '#f8f9fa';
                          e.target.style.boxShadow = 'none';
                          e.target.style.borderColor = '#dfe3e9';
                        }}
                      />
                      <FontAwesomeIcon 
                        icon={faSearch} 
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '10px',
                          color: '#818ea3',
                          transition: 'color 0.2s ease'
                        }}
                      />
                      {search && (
                        <FontAwesomeIcon 
                          icon={faTimes}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '10px',
                            color: '#818ea3',
                            cursor: 'pointer',
                            transition: 'color 0.2s ease'
                          }}
                          onClick={() => setSearch('')}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#21AA73'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#818ea3'}
                        />
                      )}
                    </div>

                    {/* Download Button */}
                    <div style={{ width: '220px', minWidth: '220px', flexShrink: 0 }}>
                      <DownloadButton
                        filename={'enforceplus_violations'}
                        fileData={generateFileData()}
                        showPdfLoading={false}
                        createPdf={() => {
                          const target = document.getElementById('content-id');
                          if (!target) return;
                          generatePDF(target);
                        }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </CardHeader>

            <CardBody className="px-4">
              {props.gettingAllViolations ? (
                <div className="text-center py-5">
                  <ActivityIndicator size="large" />
                  <p className="mt-3 text-muted">Loading violations...</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive" id="content-id">
                    <table className="table mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ color: '#818ea3', fontWeight: 500, fontSize: 13 }}>LICENSE PLATE</th>
                          <th style={{ color: '#818ea3', fontWeight: 500, fontSize: 13 }}>MAKE</th>
                          <th style={{ color: '#818ea3', fontWeight: 500, fontSize: 13 }}>MODEL</th>
                          <th style={{ color: '#818ea3', fontWeight: 500, fontSize: 13 }}>COLOR</th>
                          <th style={{ color: '#818ea3', fontWeight: 500, fontSize: 13 }}>DATE/TIME</th>
                          <th style={{ color: '#818ea3', fontWeight: 500, fontSize: 13 }}>STATE</th>
                          <th style={{ color: '#818ea3', fontWeight: 500, fontSize: 13, textAlign: 'right' }}>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterViolations().map((v, idx) => (
                          <tr key={v.id || idx}>
                            <td style={{ fontWeight: 500, color: '#495057' }}>{v.license_plate || ''}</td>
                            <td>{v.make || ''}</td>
                            <td>{v.model || ''}</td>
                            <td>{v.color || ''}</td>
                            <td>{moment(v.scanned || v.created_at).format('MM/DD/YYYY h:mm a')}</td>
                            <td>{v.license_plate_state || ''}</td>
                            <td style={{ textAlign: 'right' }}>
                              <Button
                                theme="danger"
                                size="sm"
                                outline
                                onClick={() => setViolationToRemove(v)}
                                disabled={isRemoving}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Remove Violation Confirmation Modal */}
      <Modal
        open={!!violationToRemove}
        toggle={() => !isRemoving && setViolationToRemove(null)}
        size="sm"
      >
        <ModalHeader toggle={() => !isRemoving && setViolationToRemove(null)}>
          Confirm removal
        </ModalHeader>
        <ModalBody className="p-4">
          <p style={{ fontSize: 14, color: '#495057' }}>
            Are you sure you want to remove this potential violation for{' '}
            <strong>{violationToRemove?.license_plate}</strong>?
          </p>
          <div className="d-flex justify-content-end mt-4">
            <Button
              theme="white"
              className="mr-2"
              onClick={() => setViolationToRemove(null)}
              disabled={isRemoving}
            >
              Cancel
            </Button>
            <Button
              theme="danger"
              onClick={handleConfirmRemove}
              disabled={isRemoving}
            >
              {isRemoving ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Date Picker Modal */}
      <Modal
        open={showDatePickerModal}
        toggle={() => setShowDatePickerModal(!showDatePickerModal)}
        className="date-picker-modal"
        size="lg"
      >
        <ModalHeader toggle={() => setShowDatePickerModal(false)} className="border-bottom">
          <FontAwesomeIcon icon={faCalendar} className="mr-2" style={{ color: '#21AA73' }} />
          Select Date Range
        </ModalHeader>
        <ModalBody className="p-4">
          <Row>
            <Col md="8">
              <Card className="mb-0" style={{ boxShadow: 'none', border: '1px solid #eaedf3' }}>
                <CardBody className="p-4">
                  <div className="d-flex flex-column" style={{ gap: '16px' }}>
                    <div>
                      <label className="d-block mb-2" style={{ fontSize: '13px', color: '#818ea3', fontWeight: '500' }}>Start Date</label>
                      <Button 
                        block
                        theme="white"
                        style={{
                          borderColor: activeCalendar === 'start' ? '#21AA73' : '#e1e5eb',
                          backgroundColor: '#fff',
                          textAlign: 'left',
                          position: 'relative',
                          height: '38px',
                          boxShadow: activeCalendar === 'start' ? '0 0 0 3px rgba(33, 170, 115, 0.1)' : 'none'
                        }}
                        onClick={() => setActiveCalendar(activeCalendar === 'start' ? null : 'start')}
                      >
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faCalendar} className="mr-2" style={{ color: '#21AA73' }} />
                          <span style={{ color: '#495057' }}>
                            {startDate ? moment(startDate).format('MMM D, YYYY') : 'Select start date'}
                          </span>
                        </div>
                      </Button>
                      {activeCalendar === 'start' && (
                        <div style={{ 
                          position: 'absolute', 
                          zIndex: 1000,
                          marginTop: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                          border: '1px solid #eaedf3'
                        }}>
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => {
                              handleDateChange('start', date);
                              setActiveCalendar('end');
                            }}
                            maxDate={endDate || new Date()}
                            inline
                            calendarClassName="single-date-calendar"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="d-block mb-2" style={{ fontSize: '13px', color: '#818ea3', fontWeight: '500' }}>End Date</label>
                      <Button 
                        block
                        theme="white"
                        style={{
                          borderColor: activeCalendar === 'end' ? '#21AA73' : '#e1e5eb',
                          backgroundColor: '#fff',
                          textAlign: 'left',
                          position: 'relative',
                          height: '38px',
                          boxShadow: activeCalendar === 'end' ? '0 0 0 3px rgba(33, 170, 115, 0.1)' : 'none'
                        }}
                        onClick={() => setActiveCalendar(activeCalendar === 'end' ? null : 'end')}
                      >
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faCalendar} className="mr-2" style={{ color: '#21AA73' }} />
                          <span style={{ color: '#495057' }}>
                            {endDate ? moment(endDate).format('MMM D, YYYY') : 'Select end date'}
                          </span>
                        </div>
                      </Button>
                      {activeCalendar === 'end' && (
                        <div style={{ 
                          position: 'absolute', 
                          zIndex: 1000,
                          marginTop: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                          border: '1px solid #eaedf3'
                        }}>
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => {
                              handleDateChange('end', date);
                              setActiveCalendar(null);
                              setShowDatePickerModal(false);
                            }}
                            minDate={startDate}
                            maxDate={new Date()}
                            inline
                            calendarClassName="single-date-calendar"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <style>
                    {`
                      .single-date-calendar { border: none !important; width: 100%; }
                      .single-date-calendar .react-datepicker__header { background-color: #fff; border-bottom: 1px solid #eaedf3; padding-top: 10px; }
                      .single-date-calendar .react-datepicker__current-month { color: #495057; font-weight: 600; font-size: 14px; }
                      .single-date-calendar .react-datepicker__day-name { color: #818ea3; font-size: 12px; margin: 3px; }
                      .single-date-calendar .react-datepicker__day { color: #495057; margin: 3px; font-size: 13px; width: 32px; height: 32px; line-height: 32px; }
                      .single-date-calendar .react-datepicker__day:hover { background-color: rgba(33, 170, 115, 0.1); border-radius: 50%; }
                      .single-date-calendar .react-datepicker__day--selected { background-color: #21AA73; border-radius: 50%; color: white; }
                      .single-date-calendar .react-datepicker__day--keyboard-selected { background-color: rgba(33, 170, 115, 0.2); border-radius: 50%; color: #21AA73; }
                      .single-date-calendar .react-datepicker__navigation { top: 8px; }
                      .single-date-calendar .react-datepicker__navigation-icon::before { border-color: #21AA73; }
                    `}
                  </style>
                </CardBody>
              </Card>
            </Col>
            <Col md="4">
              <Card className="mb-0" style={{ boxShadow: 'none', border: '1px solid #eaedf3' }}>
                <CardHeader className="border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                  <h6 className="m-0" style={{ fontSize: '13px', fontWeight: '600', color: '#495057' }}>Quick Select</h6>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="d-flex flex-column" style={{ gap: '8px' }}>
                    <Button theme="light" size="sm" block style={{ fontSize: '13px', color: '#21AA73', borderColor: 'rgba(33, 170, 115, 0.3)', textAlign: 'left', padding: '10px 12px', backgroundColor: '#fff' }} onClick={() => handlePresetClick('today')}>Today</Button>
                    <Button theme="light" size="sm" block style={{ fontSize: '13px', color: '#21AA73', borderColor: 'rgba(33, 170, 115, 0.3)', textAlign: 'left', padding: '10px 12px', backgroundColor: '#fff' }} onClick={() => handlePresetClick('yesterday')}>Yesterday</Button>
                    <Button theme="light" size="sm" block style={{ fontSize: '13px', color: '#21AA73', borderColor: 'rgba(33, 170, 115, 0.3)', textAlign: 'left', padding: '10px 12px', backgroundColor: '#fff' }} onClick={() => handlePresetClick('lastWeek')}>Last 7 days</Button>
                    <Button theme="light" size="sm" block style={{ fontSize: '13px', color: '#21AA73', borderColor: 'rgba(33, 170, 115, 0.3)', textAlign: 'left', padding: '10px 12px', backgroundColor: '#fff' }} onClick={() => handlePresetClick('lastMonth')}>Last 30 days</Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default PotentialViolations;